import { useAuthStore } from '@/stores/useAuthStore';

/**
 * Pure function that resolves role conflicts.
 *
 * Precedence (most restrictive wins): viewer > frontwindow > user/admin.
 * All accounts carry the `user` role.  Business rules:
 *   frontwindow + user            → Front Window only, NO conflict
 *   frontwindow + user + admin    → Front Window only, conflict
 *   frontwindow + user + viewer   → Viewer only, conflict
 *   fw + user + viewer + admin    → Viewer only, conflict
 */
export const resolveRoles = (roles: string[]) => {
  const hasViewer = roles.includes('viewer');
  const hasFrontWindow = roles.includes('frontwindow');

  // Viewer overrides everything; frontwindow overrides user/admin.
  const isFrontWindowOnly = hasFrontWindow && !hasViewer;
  const hasConflict =
    (hasViewer && roles.some((r) => r === 'admin' || r === 'frontwindow')) ||
    (isFrontWindowOnly && roles.some((r) => r === 'admin'));

  const isEffectiveRole = (role: string): boolean => {
    if (!roles.includes(role)) return false;
    if (hasViewer && role !== 'viewer') return false;
    if (isFrontWindowOnly && role !== 'frontwindow') return false;
    return true;
  };

  return {
    isViewer: hasViewer,
    isFrontWindowOnly,
    hasConflict,
    isEffectiveRole,
  };
};

export const useAuth = () => {
  const {
    user,
    accessToken,
    idToken,
    refreshToken,
    isAuthenticated,
    isLoading,
    login,
    clearAuth,
    refreshTokens,
    setTokens
  } = useAuthStore();

  const userRoles = user?.roles ?? [];
  const {
    isViewer,
    isFrontWindowOnly,
    hasConflict,
    isEffectiveRole,
  } = resolveRoles(userRoles);

  return {
    // State
    user,
    accessToken,
    idToken,
    refreshToken,
    isAuthenticated,
    isLoading,
    
    // Actions
    login,
    clearAuth,
    refreshTokens,
    setTokens,
    
    // Computed — viewer wins when conflict exists; frontwindow wins over user/admin
    hasRole: isEffectiveRole,
    hasAnyRole: (roles: string[]) => roles.some(isEffectiveRole),
    isAdmin: isEffectiveRole('admin'),
    isUser: isEffectiveRole('user'),
    isViewer,
    isFrontWindowOnly,
    hasRoleConflict: hasConflict,
  };
};
